import { useMemo } from "react";
import { useCasinoCategories } from "@/features/casino/hooks/useCasinoCategories";
import { useCasinoCategoryCounts } from "@/features/casino/hooks/useCasinoCategoryCounts";
import { useRecentlyPlayedGames } from "@/features/casino/hooks/useRecentlyPlayedGames";
import { getCategoriesWithGames } from "@/features/casino/utils/helpers";

/**
 * The active categories that have at least one visible game for this user.
 */
const useAvailableCasinoCategories = (
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) => {
  const enabled = options.enabled ?? true;

  const { categories, isLoading: isLoadingCategories } =
    useCasinoCategories(enabled);
  const {
    categoryCountsByProduct,
    country,
    isLoading: isLoadingCounts,
  } = useCasinoCategoryCounts(undefined, undefined, options);

  const { recentlyPlayedGames, isLoadingRecentlyPlayed } =
    useRecentlyPlayedGames(country, { enabled });

  const availableCategories = useMemo(
    () =>
      getCategoriesWithGames(
        categoryCountsByProduct,
        categories,
        (recentlyPlayedGames ?? []).length > 0,
      ),
    [categoryCountsByProduct, categories, recentlyPlayedGames],
  );

  return {
    availableCategories,
    isLoading:
      enabled &&
      (isLoadingCategories || isLoadingCounts || isLoadingRecentlyPlayed),
  } as const;
};

export { useAvailableCasinoCategories };
